import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { X, Info } from 'lucide-react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, useGLTF } from '@react-three/drei';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import * as THREE from 'three';

gsap.registerPlugin(ScrollTrigger);

function PreWorkoutModel({ modelRef }: { modelRef: React.MutableRefObject<THREE.Group | null> }) {
  const gltf = useGLTF('https://aogzcyknlimbgcwjukah.supabase.co/storage/v1/object/public/3d-models/protien_jar.glb');
  const scene = Array.isArray(gltf) ? gltf[0].scene : gltf.scene;
  return <primitive ref={modelRef as any} object={scene} scale={[4.788, 4.788, 4.788]} position={[0, 0.5, 0]} />;
}

const InteractiveProductImage = () => {
  const [showFacts, setShowFacts] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const modelRef = useRef<THREE.Group | null>(null);

  const supplementFacts = [
    { name: "L-Citrulline malate (2:1)", amount: "15,000mg", per1Scoop: "7,500mg", highlight: true },
    { name: "L-Citrulline", amount: "10,000mg", per1Scoop: "5,000mg", subItem: true },
    { name: "Malic Acid", amount: "5,000mg", per1Scoop: "2,500mg", subItem: true },
    { name: "Betaine Anhydrous", amount: "5,000mg", per1Scoop: "2,500mg", highlight: true },
    { name: "Taurine", amount: "3,000mg", per1Scoop: "1,500mg", highlight: true },
    { name: "L-Tyrosine", amount: "3,000mg", per1Scoop: "1,500mg", highlight: true },
    { name: "Bitter Orange Extract", amount: "1,053mg", per1Scoop: "526mg" },
    { name: "Pink Himalayan Sea Salt", amount: "500mg", per1Scoop: "250mg" },
    { name: "Niacin", amount: "16mg", per1Scoop: "8mg", dv: "100%" },
    { name: "Sodium", amount: "200mg", per1Scoop: "100mg" },
    { name: "Potassium", amount: "190mg", per1Scoop: "95mg", dv: "9.5%" }
  ];

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    const baseRotation = Math.PI * 1.7; // start position more to the right
    const totalTurns = 2; // faster rotation - two complete rotations

    const st = ScrollTrigger.create({
      trigger: container,
      start: 'top bottom',
      end: 'bottom top',
      scrub: 0.243, // 10% faster animation
      onUpdate: (self) => {
        const progress = self.progress;
        const targetRotation = baseRotation + progress * Math.PI * 2 * totalTurns;
        if (modelRef.current) {
          modelRef.current.rotation.y = targetRotation;
        }
      },
    });

    return () => {
      st.kill();
    };
  }, []);

  return (
    <div className="relative -mt-8 h-auto" ref={containerRef}>
      <div className="sticky top-8 h-[400px] sm:h-[500px] md:h-[600px] lg:h-[800px]">
        <div className="relative h-[400px] sm:h-[500px] md:h-[600px] lg:h-[800px] w-full bg-gradient-to-br from-brand-darkBlack/90 to-brand-darkBlack rounded-lg overflow-hidden shadow-2xl">
           <Canvas 
            camera={{ position: [0, 6, window.innerWidth < 768 ? 24 : 18], fov: 60 }}
            style={{ background: 'transparent' }}
            gl={{ alpha: true, antialias: true }}
          >
            <ambientLight intensity={0.6} />
            <directionalLight position={[8, 8, 4]} intensity={1.2} castShadow />
            <directionalLight position={[-4, -4, -2]} intensity={0.4} />
            <pointLight position={[-8, -8, -4]} intensity={0.8} color="#FFD700" />
            <PreWorkoutModel modelRef={modelRef} />
            <OrbitControls 
              enableZoom={false} 
              enablePan={false}
              enableRotate={true}
              enableDamping={true}
              dampingFactor={0.05}
              autoRotate={false}
            />
          </Canvas>
          
          {/* Info overlay */}
          <div className="absolute top-4 right-4">
            <Badge 
              variant="secondary" 
              className="bg-primary/10 text-brand-darkBlack cursor-pointer"
              onClick={() => setShowFacts(!showFacts)}
            >
              <Info className="w-3 h-3 mr-1" />
              Vis innhold
            </Badge>
          </div>

          {/* Product badge */}
          <div className="absolute -bottom-2 -right-4 bg-brand-electricBlue text-foreground px-4 py-2 rounded-lg font-bold">
            24 serveringer
          </div>
        </div>

        {/* Supplement facts overlay */}
        {showFacts && (
          <Card className="absolute inset-0 bg-muted backdrop-blur-sm border border-border animate-fade-in overflow-hidden">
            <CardContent className="p-6 h-full overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-primary">Innholdsliste</h3>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFacts(false);
                  }}
                  className="text-primary hover:text-primary transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-4 p-3 bg-primary/10 rounded-lg border border-border">
                <p className="text-primary font-semibold text-sm">
                  💊 Servingstørrelse: 1-2 skjeer | 24 serveringer per boks (1 skje)
                </p>
              </div>

              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2 text-xs font-semibold text-primary border-b border-border pb-2">
                  <span>Ingrediens</span>
                  <span className="text-center">Per 2 skjeer</span>
                  <span className="text-center">Per 1 skje</span>
                </div>

                {supplementFacts.map((fact, index) => (
                  <div key={index} className={`grid grid-cols-3 gap-2 text-sm py-2 border-b border-border/20 ${
                    fact.highlight ? 'bg-brand-electricBlue/10' : ''
                  } ${fact.subItem ? 'pl-4 text-muted-foreground' : 'text-foreground'}`}>
                    <div className="flex items-center">
                      <span className={fact.subItem ? 'text-xs' : ''}>{fact.name}</span>
                      {fact.dv && (
                        <Badge variant="outline" className="ml-2 text-xs">
                          {fact.dv} DV
                        </Badge>
                      )}
                    </div>
                    <span className="text-center font-semibold text-primary">{fact.amount}</span>
                    <span className="text-center text-muted-foreground">{fact.per1Scoop}</span>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                <p className="text-green-400 text-sm">
                  ✅ Alle ingredienser er dosert basert på kliniske studier
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default InteractiveProductImage;